import { useEffect, useState, type ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import { setupInterceptors } from '@/api/interceptors';
import { installPermissionRuntimeGuard } from '@/app/permission-runtime-guard';
import { auth } from '@/api/firebase';
import { bootstrapSession, logoutSession } from '@/api/auth';
import { useAuthStore } from '@/app/store/auth.store';

// PRD 00 §3.1 — Session Timeout: Default 60 minutes; automatic logout on inactivity.
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

interface AuthProviderProps {
  children: ReactNode;
}

// Registers axios interceptors, re-establishes the session on a hard reload, and runs the 60-min idle timer.
// BE-mediated login owns the auth lifecycle; the in-memory token is restored on reload via the backend
// refresh flow (FEATURE_005 / 04_FE B.3) — never from browser storage.
export function AuthProvider({ children }: AuthProviderProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const [booting, setBooting] = useState(true);

  // On every page load the in-memory session is empty (the token was never stored). Before rendering the app,
  // silently ask the backend to re-establish the session from the HttpOnly refresh cookie. Show a skeleton
  // until it resolves — never a flash of the login screen (04_FE B.3). Runs once (StrictMode-safe via
  // bootstrapSession's memoized single-use call).
  useEffect(() => {
    setupInterceptors(); // install before the bootstrap's GET /me so the fresh token is attached
    // SHCT-582 — PRD 21 §7.4 P-3. Installed beside the interceptors so the L2 click guard is live
    // for the very first screen; both are idempotent and survive StrictMode's double mount.
    installPermissionRuntimeGuard();
    let active = true;
    void bootstrapSession().then((result) => {
      if (!active) return;
      if (result) {
        login({
          email: result.me.email,
          name: result.me.name,
          roles: result.me.roles,
          isRoot: result.me.isRoot,
          currentAdminId: result.me.id,
          status: result.me.status,
          permissions: result.me.permissions,
          mustChangePassword: result.mustChangePassword,
        });
      }
      setBooting(false);
    });
    return () => {
      active = false;
    };
  }, [login]);

  // 60-min idle auto-logout. Drop the in-memory session AND clear the server-side refresh cookie, so a reload
  // after the idle timeout cannot re-establish the timed-out session.
  useEffect(() => {
    if (!isAuthenticated) return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Tell the server this is an idle-timeout logout so the audit row is LOGOUT_SESSION_TIMEOUT, not the
        // ordinary LOGOUT_BY_USER (SHCT-148 / FEATURE_050). The user-clicked logout + the 401 interceptor keep
        // the default USER reason.
        void logoutSession({ reason: 'SESSION_TIMEOUT' });
        void signOut(auth);
        logout();
      }, SESSION_TIMEOUT_MS);
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, reset, { passive: true }),
    );
    reset();
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, reset));
    };
  }, [isAuthenticated, logout]);

  if (booting) {
    return <BootSkeleton />;
  }
  return <>{children}</>;
}

/** Full-screen loading shell shown while the session is re-established on reload (no login-screen flash). */
function BootSkeleton() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg, #ffffff)',
      }}
    >
      <style>{'@keyframes ards-boot-spin{to{transform:rotate(360deg)}}'}</style>
      <span
        aria-hidden="true"
        style={{
          width: 36,
          height: 36,
          border: '3px solid var(--color-border, #e5e7eb)',
          borderTopColor: 'var(--color-primary, #2563eb)',
          borderRadius: '50%',
          animation: 'ards-boot-spin 0.7s linear infinite',
        }}
      />
    </div>
  );
}
